import { useEffect, useState } from "react";
import { HttpMethod, callApi } from "src/services/apiService";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";

// --- DNC interfaces (mirror admin/Account/Usage response) ---
interface IScrubPool {
  poolId: number;
  scrubsGiven: number;
  scrubsAtPeriodStart: number;
  liveRemaining: number;
  expiresOn: string;
}

interface IReassignedPool {
  poolId: number;
  given: number;
  remainingAtPeriodStart: number;
  liveRemaining: number;
  expiresOn: string;
}

interface IReassignedPoolGroup {
  productKey: string;
  productLabel: string;
  usageThisPeriod: number;
  pools: IReassignedPool[];
}

interface IAccountUsage {
  acctId: string;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  billableScrubs: number;
  freeScrubsPerMonth: number;
  scrubPools: IScrubPool[];
  reassignedPools: IReassignedPoolGroup[];
}

interface IReassignedProduct {
  productKey: string;
  productLabel: string;
  usageThisPeriod: number;
  includedPerMonth: number;
  isTiered: boolean;
  overageThisPeriod: number;
  pools: IReassignedPool[];
}

interface IReassignedUsage {
  acctId: string;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  anyFeatureEnabled: boolean;
  products: IReassignedProduct[];
}

interface ITrustCallService {
  serviceKey: string;
  serviceLabel: string;
  activeCount: number;
  includedPerMonth: number;
}

interface ITrustCallUsage {
  acctId: string;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  featureEnabled: boolean;
  services: ITrustCallService[];
}

function formatPeriodDate(iso: string): string {
  // Long-form date for human readability ("May 2, 2026"). Backend returns
  // DateTime ISO strings; time-of-day is irrelevant for billing period UI.
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

interface AccountUsagePanelProps {
  // "admin/Account" for the logged-in account view (/reports/account-usage).
  // "sales/lead-lookup" for the sales rep expand-row.
  endpointBase: string;
  // Required for the sales/lead-lookup variant; passed as ?customerAcctId=.
  // Omit (or pass empty) on the admin/Account variant — the API infers the
  // logged-in user's acctId.
  customerAcctId?: string;
  // Sysadmin impersonation tail (e.g. "&asUser=Some%20Rep") appended to the
  // sales URLs. Empty string for the normal case.
  asUserSuffix?: string;
  // Render the "Email me when ready" form on the DNC card. True on the
  // /reports/account-usage page; false on the Lead Lookup expand-row (sales
  // reps scan many accounts — not waiting on one).
  showDncEmailForm?: boolean;
  // Tunes the empty-state copy:
  //   "account"   — the account user is looking at their own page; pitch the
  //                 product + "Talk to sales" mailto.
  //   "sales-rep" — a sales rep is scanning an account; give them the raw
  //                 "not enabled" fact without trying to sell to them.
  // Defaults to "account".
  audience?: "account" | "sales-rep";
}

/**
 * The three usage cards shown on /reports/account-usage and the Lead Lookup
 * expand-row. The compute lives in AccountUsageService on the backend; this
 * component renders whatever the three endpoints return. Both pages call the
 * same code path so the displayed numbers always match.
 */
const AccountUsagePanel: React.FC<AccountUsagePanelProps> = ({
  endpointBase,
  customerAcctId,
  asUserSuffix,
  showDncEmailForm,
  audience = "account",
}) => {
  const { userEmail } = useAppSelector(selectAuth);

  const [usage, setUsage] = useState<IAccountUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [emailNotifyAddress, setEmailNotifyAddress] = useState(userEmail ?? "");
  const [emailNotifyStatus, setEmailNotifyStatus] = useState<
    "idle" | "sending" | "queued" | "error"
  >("idle");
  const [emailNotifyMessage, setEmailNotifyMessage] = useState("");

  const [reassignedUsage, setReassignedUsage] =
    useState<IReassignedUsage | null>(null);
  const [reassignedUsageLoading, setReassignedUsageLoading] = useState(true);
  const [reassignedUsageError, setReassignedUsageError] = useState<string | null>(
    null,
  );

  const [trustCallUsage, setTrustCallUsage] = useState<ITrustCallUsage | null>(
    null,
  );
  const [trustCallLoading, setTrustCallLoading] = useState(true);
  const [trustCallError, setTrustCallError] = useState<string | null>(null);

  // Build the endpoint URL. The sales/lead-lookup variant needs a
  // customerAcctId query param + optional asUser. The admin/Account variant
  // hits the bare path.
  const buildUrl = (suffix: string) => {
    if (customerAcctId) {
      return `${endpointBase}/${suffix}?customerAcctId=${encodeURIComponent(
        customerAcctId,
      )}${asUserSuffix ?? ""}`;
    }
    return `${endpointBase}/${suffix}`;
  };

  // DNC fetch — slow on first hit for pool accounts (alt-calc path); the
  // Email-Me-When-Ready form (when shown) lets users walk away.
  useEffect(() => {
    const fetchUsage = async () => {
      setUsageLoading(true);
      try {
        const response = (await callApi(
          buildUrl("Usage"),
          HttpMethod.GET,
        )) as IAccountUsage;
        setUsage(response);
        setUsageError(null);
      } catch (error) {
        console.error("Failed to fetch account usage:", error);
        setUsageError("Failed to load usage.");
      } finally {
        setUsageLoading(false);
      }
    };
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointBase, customerAcctId]);

  useEffect(() => {
    const fetchReassignedUsage = async () => {
      setReassignedUsageLoading(true);
      try {
        const response = (await callApi(
          buildUrl("ReassignedUsage"),
          HttpMethod.GET,
        )) as IReassignedUsage;
        setReassignedUsage(response);
        setReassignedUsageError(null);
      } catch (error) {
        console.error("Failed to fetch reassigned usage:", error);
        setReassignedUsageError("Failed to load reassigned usage.");
      } finally {
        setReassignedUsageLoading(false);
      }
    };
    fetchReassignedUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointBase, customerAcctId]);

  useEffect(() => {
    const fetchTrustCall = async () => {
      setTrustCallLoading(true);
      try {
        const response = (await callApi(
          buildUrl("TrustCallUsage"),
          HttpMethod.GET,
        )) as ITrustCallUsage;
        setTrustCallUsage(response);
        setTrustCallError(null);
      } catch (error) {
        console.error("Failed to fetch TrustCall usage:", error);
        setTrustCallError("Failed to load TrustCall usage.");
      } finally {
        setTrustCallLoading(false);
      }
    };
    fetchTrustCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointBase, customerAcctId]);

  useEffect(() => {
    if (userEmail && !emailNotifyAddress) {
      setEmailNotifyAddress(userEmail);
    }
  }, [userEmail, emailNotifyAddress]);

  const reassignedShowsUpsell =
    !reassignedUsageLoading &&
    !reassignedUsageError &&
    reassignedUsage &&
    !reassignedUsage.anyFeatureEnabled;

  const trustCallShowsUpsell =
    !trustCallLoading &&
    !trustCallError &&
    trustCallUsage &&
    !trustCallUsage.featureEnabled;

  // mailto body includes the acctId so the rep knows which account opened it
  const acctIdForCta = customerAcctId ?? usage?.acctId ?? "";
  const salesMailto = (subject: string) =>
    `mailto:sales@dnc.com?subject=${encodeURIComponent(subject)}` +
    (acctIdForCta
      ? `&body=${encodeURIComponent(`Account: ${acctIdForCta}`)}`
      : "");

  return (
    <>
      {/* DNC Usage */}
      <div className="card mb-3">
        <div className="card-header">Do Not Call Usage This Period</div>
        <div className="card-body">
          {usageLoading ? (
            <>
              <div className="d-flex align-items-center">
                <span
                  className="spinner-border spinner-border-sm text-secondary"
                  role="status"
                  aria-label="Loading usage"
                />
                <span
                  className="text-muted ms-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  This can take up to a minute on first load.
                </span>
              </div>
              {showDncEmailForm &&
                (emailNotifyStatus === "queued" ? (
                  <div
                    className="text-muted mt-2"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Email queued for <code>{emailNotifyAddress}</code> — you can
                    close this page.
                  </div>
                ) : (
                  <div className="mt-2">
                    <label
                      className="form-label mb-1"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Email me when ready:
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 280 }}
                        value={emailNotifyAddress}
                        onChange={(e) => setEmailNotifyAddress(e.target.value)}
                        disabled={emailNotifyStatus === "sending"}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={
                          emailNotifyStatus === "sending" ||
                          !emailNotifyAddress.trim()
                        }
                        onClick={async () => {
                          setEmailNotifyStatus("sending");
                          setEmailNotifyMessage("");
                          try {
                            await callApi(
                              "admin/Account/Usage/EmailWhenReady",
                              HttpMethod.POST,
                              { email: emailNotifyAddress.trim() },
                            );
                            setEmailNotifyStatus("queued");
                          } catch (err: any) {
                            setEmailNotifyStatus("error");
                            setEmailNotifyMessage(
                              err?.message ?? "Failed to queue email.",
                            );
                          }
                        }}
                      >
                        Send
                      </button>
                    </div>
                    {emailNotifyStatus === "error" && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {emailNotifyMessage}
                      </div>
                    )}
                  </div>
                ))}
            </>
          ) : usageError ? (
            <span className="text-muted">{usageError}</span>
          ) : usage ? (
            <>
              <div className="d-flex align-items-baseline">
                <span className="fw-bold me-2">Billable scrubs:</span>
                <span>{(usage.billableScrubs ?? 0).toLocaleString()}</span>
              </div>
              {usage.billingPeriodStart && usage.billingPeriodEnd && (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Billing period:{" "}
                  {formatPeriodDate(usage.billingPeriodStart)} -{" "}
                  {formatPeriodDate(usage.billingPeriodEnd)}
                </div>
              )}
              {usage.freeScrubsPerMonth > 0 && (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Monthly scrubs included in plan:{" "}
                  {usage.freeScrubsPerMonth.toLocaleString()}
                </div>
              )}

              {usage.scrubPools && usage.scrubPools.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-1">Scrub Pool</div>
                  <ul className="list-unstyled mb-0">
                    {usage.scrubPools.map((p) => (
                      <li key={p.poolId}>
                        {p.liveRemaining.toLocaleString()} remaining of{" "}
                        {p.scrubsGiven.toLocaleString()}
                        <span className="text-muted">
                          {" "}
                          — expires {formatPeriodDate(p.expiresOn)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(usage.reassignedPools ?? []).map((group) => (
                <div className="mt-3" key={group.productKey}>
                  <div className="fw-bold mb-1">
                    Reassigned Pool — {group.productLabel}
                  </div>
                  <ul className="list-unstyled mb-0">
                    {group.pools.map((p) => (
                      <li key={p.poolId}>
                        {p.liveRemaining.toLocaleString()} remaining of{" "}
                        {p.given.toLocaleString()}
                        <span className="text-muted">
                          {" "}
                          — expires {formatPeriodDate(p.expiresOn)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </div>

      {/* Reassigned Usage */}
      <div className="card mb-3">
        <div className="card-header">Reassigned Usage This Period</div>
        <div className="card-body">
          {reassignedUsageLoading ? (
            <span
              className="spinner-border spinner-border-sm text-secondary"
              role="status"
              aria-label="Loading reassigned usage"
            />
          ) : reassignedUsageError ? (
            <span className="text-muted">{reassignedUsageError}</span>
          ) : reassignedShowsUpsell ? (
            audience === "sales-rep" ? (
              <p className="text-muted mb-0">
                No reassigned products enabled on this account.
              </p>
            ) : (
              <>
                <p className="mb-2">
                  Verify the original consenter still owns each phone number
                  before you call. Reassigned-number checks help you avoid
                  TCPA litigation from reassigned numbers.
                </p>
                <a
                  className="btn btn-sm btn-outline-primary"
                  href={salesMailto("Reassigned number scrubbing inquiry")}
                >
                  Talk to sales
                </a>
              </>
            )
          ) : reassignedUsage ? (
            <>
              {reassignedUsage.billingPeriodStart &&
                reassignedUsage.billingPeriodEnd && (
                  <div
                    className="text-muted mb-2"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Billing period:{" "}
                    {formatPeriodDate(reassignedUsage.billingPeriodStart)} -{" "}
                    {formatPeriodDate(reassignedUsage.billingPeriodEnd)}
                  </div>
                )}
              {reassignedUsage.products.map((p) => (
                <div className="mt-3" key={p.productKey}>
                  <div className="fw-bold">{p.productLabel}</div>
                  <div>
                    Used this period: {p.usageThisPeriod.toLocaleString()}
                  </div>
                  {p.isTiered ? (
                    <div
                      className="text-muted"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Tiered pricing — see invoice for billing detail.
                    </div>
                  ) : p.includedPerMonth > 0 ? (
                    <>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.85rem" }}
                      >
                        Included in plan:{" "}
                        {p.includedPerMonth.toLocaleString()}
                      </div>
                      {p.overageThisPeriod > 0 && (
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Overage: {p.overageThisPeriod.toLocaleString()}
                        </div>
                      )}
                    </>
                  ) : null}
                  {p.pools.length > 0 && (
                    <div className="mt-1">
                      <div className="fw-bold" style={{ fontSize: "0.85rem" }}>
                        Reassigned Pool
                      </div>
                      <ul className="list-unstyled mb-0">
                        {p.pools.map((pool) => (
                          <li key={pool.poolId}>
                            {pool.liveRemaining.toLocaleString()} remaining of{" "}
                            {pool.given.toLocaleString()}
                            <span className="text-muted">
                              {" "}
                              — expires {formatPeriodDate(pool.expiresOn)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : null}
        </div>
      </div>

      {/* TrustCall Usage */}
      <div className="card mb-3">
        <div className="card-header">TrustCall Usage This Period</div>
        <div className="card-body">
          {trustCallLoading ? (
            <span
              className="spinner-border spinner-border-sm text-secondary"
              role="status"
              aria-label="Loading TrustCall usage"
            />
          ) : trustCallError ? (
            <span className="text-muted">{trustCallError}</span>
          ) : trustCallShowsUpsell ? (
            audience === "sales-rep" ? (
              <p className="text-muted mb-0">
                TrustCall not enabled on this account.
              </p>
            ) : (
              <>
                <p className="mb-2">
                  Monitor your phone numbers for spam labels across Verizon,
                  AT&amp;T, and T-Mobile. Catch spam tags early so they
                  don't tank your answer rates.
                </p>
                <a
                  className="btn btn-sm btn-outline-primary"
                  href={salesMailto("TrustCall inquiry")}
                >
                  Talk to sales
                </a>
              </>
            )
          ) : trustCallUsage ? (
            <>
              {trustCallUsage.billingPeriodStart &&
                trustCallUsage.billingPeriodEnd && (
                  <div
                    className="text-muted mb-2"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Billing period:{" "}
                    {formatPeriodDate(trustCallUsage.billingPeriodStart)} -{" "}
                    {formatPeriodDate(trustCallUsage.billingPeriodEnd)}
                  </div>
                )}
              {trustCallUsage.services.map((s) => (
                <div className="mt-2" key={s.serviceKey}>
                  <div className="fw-bold">{s.serviceLabel}</div>
                  <div>Active: {s.activeCount.toLocaleString()}</div>
                  <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                    Included in plan: {s.includedPerMonth.toLocaleString()}
                  </div>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default AccountUsagePanel;
